import React from 'react'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Avatar } from './avatar'
import { displayPersonName } from '@/lib/personName'

export interface MemberOption {
  id: string
  name: string
  role: string
  tagline?: string
  jobTitle?: string | null
  avatar?: string
  type: string
}

interface MemberAutocompleteProps {
  selectedMembers?: MemberOption[]
  onMembersChange: (members: MemberOption[]) => void
  onSearch: (search: string) => Promise<MemberOption[]>
  placeholder?: string
  disabled?: boolean
}

export const MemberAutocomplete = ({
  selectedMembers: selectedMembersProp,
  onMembersChange,
  onSearch,
  placeholder,
  disabled = false
}: MemberAutocompleteProps) => {
  const { t } = useTranslation('common')
  const selectedMembers = selectedMembersProp || []
  const [inputValue, setInputValue] = React.useState('')
  const [suggestions, setSuggestions] = React.useState<MemberOption[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [showSuggestions, setShowSuggestions] = React.useState(false)
  const [hasSearched, setHasSearched] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const debounceTimer = React.useRef<NodeJS.Timeout>()

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleInputChange = async (value: string) => {
    setInputValue(value)

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    if (value.trim().length < 2) {
      setSuggestions([])
      setShowSuggestions(false)
      setHasSearched(false)
      return
    }

    debounceTimer.current = setTimeout(async () => {
      setIsLoading(true)
      setShowSuggestions(true)
      try {
        const results = await onSearch(value.trim())
        const filtered = results.filter(
          member => !selectedMembers.some(selected => selected.id === member.id)
        )
        setSuggestions(filtered)
        setHasSearched(true)
      } catch (error) {
        console.error('Error searching members:', error)
        setSuggestions([])
        setHasSearched(true)
      } finally {
        setIsLoading(false)
      }
    }, 300)
  }

  const handleSelectMember = (member: MemberOption) => {
    onMembersChange([...selectedMembers, member])
    setInputValue('')
    setSuggestions([])
    setShowSuggestions(false)
    setHasSearched(false)
    inputRef.current?.focus()
  }

  const handleRemoveMember = (memberId: string) => {
    onMembersChange(selectedMembers.filter(p => p.id !== memberId))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !inputValue && selectedMembers.length > 0) {
      onMembersChange(selectedMembers.slice(0, -1))
    }
  }

  return (
    <div ref={wrapperRef} className="relative">
      <div className="border rounded-lg p-2 bg-white focus-within:ring-2 focus-within:ring-primary-200 focus-within:border-primary min-h-[42px] transition-none">
        <div className="flex flex-wrap gap-2 items-center">
          {selectedMembers.map((member) => (
            <div
              key={member.id}
              className="flex items-center gap-2 bg-blue-50 text-blue-700 px-2 py-1 rounded-md text-sm group shrink-0"
            >
              <Avatar
                src={member.avatar}
                alt={displayPersonName(t, member.name)}
                fallback={member.name}
                className="h-5 w-5"
              />
              <span className="font-medium">{displayPersonName(t, member.name)}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemoveMember(member.id)}
                  className="opacity-60 group-hover:opacity-100 hover:text-blue-800 transition-opacity"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (suggestions.length > 0) {
                setShowSuggestions(true)
              }
            }}
            className="flex-1 min-w-[120px] outline-none text-sm bg-transparent"
            placeholder={selectedMembers.length === 0 ? placeholder : ""}
            disabled={disabled}
          />
        </div>
      </div>

      {showSuggestions && (isLoading || suggestions.length > 0 || hasSearched) && (
        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-[300px] overflow-y-auto">
          {isLoading ? (
            <div className="p-3 text-sm text-gray-500 text-center">
              {t('searching')}
            </div>
          ) : suggestions.length > 0 ? (
            <div className="py-1">
              {suggestions.map((member) => (
                <button
                  key={member.id}
                  type="button"
                  onClick={() => handleSelectMember(member)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-50 transition-colors flex items-center gap-3"
                >
                  <Avatar
                    src={member.avatar}
                    alt={displayPersonName(t, member.name)}
                    fallback={member.name}
                    className="h-8 w-8"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{displayPersonName(t, member.name)}</div>
                    {/* Job title first: it says who the person is in one
                        glance; the tagline follows when there is room. */}
                    {(member.jobTitle || member.tagline) && (
                      <div className="text-xs text-gray-500 truncate">
                        {[member.jobTitle, member.tagline].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 capitalize">{member.role}</div>
                </button>
              ))}
            </div>
          ) : hasSearched ? (
            <div className="p-4 text-center">
              <div className="text-sm text-gray-500 mb-1">{t('noMembersFound')}</div>
              <div className="text-xs text-gray-400">{t('tryDifferentSearch')}</div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
